import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './Settings.css';

const Settings = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    email_notifications: true,
    course_updates: true,
    promotional_emails: false,
    weekly_digest: true,
    dark_mode: false,
    language: 'en',
    timezone: 'UTC',
  });
  const [loading, setLoading] = useState(false);
  const [gdprLoading, setGdprLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/notifications/preferences');
      if (response.data) {
        setSettings((prev) => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleToggle = (key) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleChange = (e) => {
    setSettings((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleExportData = async () => {
    setGdprLoading(true);
    try {
      const res = await api.get('/auth/export', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'my-lms-data.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to export data. Please try again.' });
    } finally {
      setGdprLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm(
      'This will permanently delete your account and remove your personal data. This cannot be undone. Continue?'
    )) return;
    setGdprLoading(true);
    try {
      await api.delete('/auth/me');
      logout();
      navigate('/login');
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to delete account.' });
      setGdprLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await api.put('/notifications/preferences', settings);
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to save settings',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page settings-page">
      <div className="container">
        <h1>Settings</h1>

        {message.text && (
          <div className={`message ${message.type}`}>{message.text}</div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Notification Settings */}
          <div className="settings-section">
            <h2>Notification Preferences</h2>
            <p className="section-description">
              Manage how you receive notifications and updates.
            </p>

            <div className="setting-item">
              <div className="setting-info">
                <h3>Email Notifications</h3>
                <p>Receive important updates via email</p>
              </div>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={settings.email_notifications}
                  onChange={() => handleToggle('email_notifications')}
                />
                <span className="slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <h3>Course Updates</h3>
                <p>Get notified when courses you're enrolled in are updated</p>
              </div>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={settings.course_updates}
                  onChange={() => handleToggle('course_updates')}
                />
                <span className="slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <h3>Weekly Digest</h3>
                <p>Receive a weekly summary of your learning progress</p>
              </div>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={settings.weekly_digest}
                  onChange={() => handleToggle('weekly_digest')}
                />
                <span className="slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <h3>Promotional Emails</h3>
                <p>Receive offers, discounts, and new course announcements</p>
              </div>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={settings.promotional_emails}
                  onChange={() => handleToggle('promotional_emails')}
                />
                <span className="slider"></span>
              </label>
            </div>
          </div>

          {/* Display Settings */}
          <div className="settings-section">
            <h2>Display Preferences</h2>
            <p className="section-description">
              Customize your learning experience.
            </p>

            <div className="setting-item">
              <div className="setting-info">
                <h3>Dark Mode</h3>
                <p>Use dark theme for the interface</p>
              </div>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={settings.dark_mode}
                  onChange={() => handleToggle('dark_mode')}
                />
                <span className="slider"></span>
              </label>
            </div>

            <div className="setting-item select-item">
              <div className="setting-info">
                <h3>Language</h3>
                <p>Select your preferred language</p>
              </div>
              <select
                name="language"
                value={settings.language}
                onChange={handleChange}
              >
                <option value="en">English</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="de">German</option>
                <option value="ar">Arabic</option>
                <option value="ur">Urdu</option>
              </select>
            </div>

            <div className="setting-item select-item">
              <div className="setting-info">
                <h3>Timezone</h3>
                <p>Set your local timezone</p>
              </div>
              <select
                name="timezone"
                value={settings.timezone}
                onChange={handleChange}
              >
                <option value="UTC">UTC</option>
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="Europe/London">London (GMT)</option>
                <option value="Europe/Paris">Paris (CET)</option>
                <option value="Asia/Dubai">Dubai (GST)</option>
                <option value="Asia/Karachi">Karachi (PKT)</option>
                <option value="Asia/Tokyo">Tokyo (JST)</option>
              </select>
            </div>
          </div>

          {/* Privacy Settings */}
          <div className="settings-section">
            <h2>Privacy</h2>
            <p className="section-description">
              Control your privacy and data settings.
            </p>

            <div className="privacy-options">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleExportData}
                disabled={gdprLoading}
              >
                {gdprLoading ? 'Exporting...' : 'Download My Data'}
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={handleDeleteAccount}
                disabled={gdprLoading}
              >
                Delete Account
              </button>
            </div>
          </div>

          <div className="settings-actions">
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Settings;
